// Table processing module for SOURCE tables: preprocess and manipulation facade
import { logError, logWarning, logInfo } from "../../../services/logging/log-handler.js";

export type WorkingDocument = any;
export type WorkingReference = any;

// Re-export manipulation helpers so consumers can import from one place
export {
  convertExtractToWorking,
  convertWorkingToExtract,
  getCurrentVersion,
  updateCellValue,
  addRow,
  addColumn,
  removeRow,
  removeColumn,
  renameColumn,
  resetToInitial
} from './data-conversion-utils.js';

// Import getCurrentVersion for use in getSourceData2D (need to import separately to use in function)
import { getCurrentVersion as getCurrentVersionUtil } from './data-conversion-utils.js';
import { generateUUID } from './uuid-utils.js';

/**
 * Convert working format table to 2D array for AG Grid
 * @param tableData - Working format table with columns and rows
 * @returns 2D array with headers in first row
 */
export function workingTableTo2DArray(tableData: any): any[][] {
  if (!tableData?.columns || !tableData?.rows) return [];
  
  const result: any[][] = [];
  
  // Header row - use column labels
  const headers = tableData.columns.map((col: any) => col.label);
  result.push(headers);
  
  // Data rows
  tableData.rows.forEach((row: any) => {
    const dataRow: any[] = [];
    tableData.columns.forEach((col: any) => {
      const cell = row.cells?.find((c: any) => c.id === col.id);
      dataRow.push(cell?.value || '');
    });
    result.push(dataRow);
  });
  
  return result;
}

/**
 * Get source table data as 2D array from working document
 * @param workingData - Working document
 * @param tableIndex - Table index
 * @param targetId - Target ID
 * @returns 2D array with headers in first row
 */
export function getSourceData2D(
  workingData: WorkingDocument,
  tableIndex: number,
  targetId: string
): any[][] {
  const currentData = getCurrentVersionUtil(workingData, tableIndex, targetId);
  if (!currentData) return [];
  return workingTableTo2DArray(currentData);
}

/**
 * Convert reference data to working format
 * Reference working format is simpler: just rows with cells, no grouper
 * @param referenceData - Reference data from extract (e.g., bibliography with rows)
 * @param referenceStructure - Reference structure from project structure
 * @returns Working reference format
 */
export function convertReferenceToWorking(
  referenceData: any,
  referenceStructure: any
): WorkingReference {
  const result: any = {
    id: referenceData.id,
    rows: []
  };
  
  // Handle table-type references (like bibliography, treatment_arms)
  if (referenceData.rows && Array.isArray(referenceData.rows)) {
    const columns = referenceStructure?.structure?.columns || [];
    
    // If we have structure columns, use them; otherwise infer from first row
    let columnIds: string[] = [];
    if (columns.length > 0) {
      columnIds = columns.map((col: any) => col.id);
    } else if (referenceData.rows.length > 0 && referenceData.rows[0].cells) {
      columnIds = referenceData.rows[0].cells.map((cell: any) => cell.id);
    }
    
    result.rows = referenceData.rows.map((row: any) => {
      // Preserve existing row ID, or generate GUID if missing/invalid
      let rowId = row.id;
      if (!rowId || (!rowId.includes('-') && !rowId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i))) {
        // Generate GUID for new rows or rows with non-GUID IDs
        rowId = generateUUID();
      }
      
      const workingRow: any = {
        id: rowId,
        cells: []
      };
      
      // Map cells - preserve order and values
      if (columns.length > 0) {
        // Use structure columns to ensure all columns are present
        columns.forEach((col: any) => {
          const cell = row.cells?.find((c: any) => c.id === col.id);
          workingRow.cells.push({
            id: col.id,
            value: cell?.value || '',
            source: cell?.source || 'system'
          });
        });
      } else {
        // Use existing cells as-is
        row.cells?.forEach((cell: any) => {
          workingRow.cells.push({
            id: cell.id,
            value: cell.value || '',
            source: cell.source || 'system'
          });
        });
      }
      
      return workingRow;
    });
  }
  
  // Handle object-type references (like study) - not editable as tables, skip for now
  // They're displayed as vertical tables and likely don't need table editing
  
  return result;
}

// Public API: preprocess full working document once (idempotent per table.status)
export function preprocessWorkingDocument(doc: WorkingDocument): WorkingDocument {
  if (!doc || !Array.isArray(doc.tables)) return doc;
  const cloned = JSON.parse(JSON.stringify(doc));
  cloned.tables = cloned.tables.map((table: any) => preprocessTableOnce(table));
  return cloned;
}

// Normalize a single table structure according to preprocessing rules
export function preprocessTableOnce(table: any): any {
  if (table && (table.status === 'valid' || table.status === 'invalid')) {
    return table;
  }

  const result = JSON.parse(JSON.stringify(table || {}));

  // 3.2 rows must exist
  if (!result?.data?.rows || !Array.isArray(result.data.rows)) {
    result.status = 'invalid';
    result.statusMessage = 'Missing data.rows array';
    return result;
  }

  let rows: any[] = result.data.rows;

  // Determine max cells across rows
  const maxCells = rows.reduce((max: number, r: any) => {
    const count = Array.isArray(r?.cells) ? r.cells.length : 0;
    return Math.max(max, count);
  }, 0);

  // 3.1 columns may exist or not
  if (!result.data.columns || !Array.isArray(result.data.columns) || result.data.columns.length === 0) {
    // derive from first row headers; drop that row from data
    const headerRowCells: any[] = Array.isArray(rows?.[0]?.cells) ? rows[0].cells : [];
    const derivedColumns: any[] = [];
    const columnsCount = headerRowCells.length;
    for (let i = 0; i < columnsCount; i++) {
      const id = `c${i + 1}`;
      const label = headerRowCells[i]?.value ?? `c${i + 1}`;
      derivedColumns.push({ id, label });
    }
    result.data.columns = derivedColumns;
    // remove header row from rows to be normalized
    rows = rows.slice(1);
  } else {
    // ensure column ids are c1..cN sequentially and preserve labels
    result.data.columns = result.data.columns.map((col: any, idx: number) => ({
      id: `c${idx + 1}`,
      label: col?.label ?? `c${idx + 1}`
    }));
  }

  // Expand columns to match max cells if needed
  if (result.data.columns.length < maxCells) {
    const start = result.data.columns.length;
    for (let i = start; i < maxCells; i++) {
      result.data.columns.push({ id: `c${i + 1}`, label: 'NA' });
    }
  }

  const columnCount = result.data.columns.length;

  // 3.3 rows: ensure ids r1.. and cells with id/value
  let invalid = false;
  let invalidReason = '';

  result.data.rows = rows.map((row: any, index: number) => {
    const newRow: any = { id: `r${index + 1}`, cells: [] as any[] };
    const cells: any[] = Array.isArray(row?.cells) ? row.cells : [];
    if (!Array.isArray(row?.cells)) {
      invalid = true;
      invalidReason = 'Row missing cells array';
    }
    for (let i = 0; i < columnCount; i++) {
      const expectedId = `c${i + 1}`;
      const cell = cells[i];
      if (cell && typeof cell === 'object') {
        newRow.cells.push({ id: expectedId, value: cell.value ?? '' });
      } else {
        newRow.cells.push({ id: expectedId, value: '' });
      }
    }
    return newRow;
  });

  if (invalid) {
    result.status = 'invalid';
    result.statusMessage = invalidReason || 'Row structure invalid';
    return result;
  }

  result.status = 'valid';
  delete result.statusMessage;
  return result;
}

// Ensure a work section exists for a specific page/table and target id
export function ensureWorkSection(doc: any, page: number, tableName: string, targetId: string): any {
  if (!doc || !Array.isArray(doc.tables)) return doc;
  const updated = JSON.parse(JSON.stringify(doc));
  const table = updated.tables.find((t: any) => t.page === page && t.name === tableName);
  if (!table) return updated;
  if (!Array.isArray(table.work)) {
    table.work = [];
  }
  const existing = table.work.find((w: any) => w?.target === targetId);
  if (!existing) {
    // If not present, seed from data if available, otherwise create empty structure
    const dataVersion = Array.isArray((table as any).data) ? (table as any).data[0] : null;
    const base = dataVersion || { columns: [], rows: [], next_row: 'r1', next_col: 'c1' };
    table.work.push({
      target: targetId,
      next_row: base.next_row || 'r1',
      next_col: base.next_col || 'c1',
      columns: JSON.parse(JSON.stringify(base.columns || [])),
      rows: JSON.parse(JSON.stringify(base.rows || []))
    });
  }
  return updated;
}

// Pivot (transpose) a table: swap rows and columns
export function pivotTable(
  workingData: WorkingDocument,
  tableIndex: number,
  targetId: string
): void {
  // Get the table
  const table = workingData.tables[tableIndex];
  if (!table) {
    logWarning('Table not found at index:', "source-table-processing", undefined, { tableIndex });
    return;
  }

  // Find the work entry for the target
  if (!Array.isArray(table.work)) {
    logWarning('No work array found', "source-table-processing", undefined);
    return;
  }

  const workEntry = table.work.find((w: any) => w.target === targetId);
  if (!workEntry) {
    logWarning(`No work entry found for target: ${targetId}`, "source-table-processing", undefined);
    return;
  }

  if (!workEntry.columns || !workEntry.rows) {
    logWarning('Work entry missing columns or rows', "source-table-processing", undefined);
    return;
  }

  // Convert to 2D array for easier pivoting
  // First row is headers (column labels)
  const original2D: any[][] = [];
  
  // Header row
  const headers = workEntry.columns.map((col: any) => col.label);
  original2D.push(headers);
  
  // Data rows
  workEntry.rows.forEach((row: any) => {
    const dataRow: any[] = [];
    workEntry.columns.forEach((col: any) => {
      const cell = row.cells?.find((c: any) => c.id === col.id);
      dataRow.push(cell?.value || '');
    });
    original2D.push(dataRow);
  });


  // Pivot (transpose) the 2D array
  const pivoted2D: any[][] = [];
  const numCols = original2D[0] ? original2D[0].length : 0;

  // Create new rows for each original column
  for (let colIndex = 0; colIndex < numCols; colIndex++) {
    const newRow: any[] = [];
    // First element is the original column header
    if (original2D[0] && original2D[0][colIndex] !== undefined) {
      newRow.push(original2D[0][colIndex]);
    } else {
      newRow.push('');
    }
    // Add data from each original row for this column
    for (let rowIndex = 1; rowIndex < original2D.length; rowIndex++) {
      if (original2D[rowIndex] && original2D[rowIndex][colIndex] !== undefined) {
        newRow.push(original2D[rowIndex][colIndex]);
      } else {
        newRow.push('');
      }
    }
    pivoted2D.push(newRow);
  }


  // Convert pivoted 2D array back to working table format
  const pivotedHeaders = pivoted2D[0];
  const pivotedDataRows = pivoted2D.slice(1);

  // Create new columns from pivoted headers
  const newColumns = pivotedHeaders.map((header: string, index: number) => ({
    id: `c${index + 1}`,
    label: header || `Column ${index + 1}`
  }));

  // Create new rows with cells
  const newRows = pivotedDataRows.map((rowData: any[], rowIndex: number) => {
    const cells = newColumns.map((col: any, colIndex: number) => ({
      id: col.id,
      value: rowData[colIndex] || ''
    }));

    return {
      id: `r${rowIndex + 1}`,
      cells: cells
    };
  });

  // Calculate next_row and next_col based on the pivoted structure
  const newNextRow = `r${newRows.length + 1}`;
  const newNextCol = `c${newColumns.length + 1}`;

  // Update the work entry
  workEntry.columns = newColumns;
  workEntry.rows = newRows;
  workEntry.next_row = newNextRow;
  workEntry.next_col = newNextCol;

}


